import Vue from 'vue'

export default class PluginsHandler
{
    constructor(activePlugins = [], theme = 'default', treeTheme = 'default') {
        // initialize the default handler object properties
        this.init()

        // if you create a new behavior you should specify it here
        // this array is only for validation purpose
        this.predefinedBehaviors = [
            'default',
            'search',
            'checkbox',
            'collapse',
            'sortable',
            'editable',
            'clone',
            'theme-default',
            'theme-folder'
        ]

        // theme is used from plugins
        // do not set the theme after the registration of active plugins
        this.setTheme(theme)

        // tree theme is the different a theme for every app theme.
        // For example we may have "folder-style" and "flat-style" tree themes for app theme "boostrap-4"
        // treeTheme is just another behavior, but it is loaded after all active behaviors
        this.treeTheme = treeTheme

        // register all active behaviors
        this.setActivePlugins(activePlugins)

        // require all plugin css files
        this.includePluginStyles()
    }

    init() {
        this.plugins = new Map()

        this.slotsContent = {}

        this.mixins = {
            treeMixins: [],
            treeRowMixins: []
        }

        this._rowIf = ''
        this._rowShow = ''

        this._rowSubNodesIf = ''
        this._rowSubNodesShow = ''
    }

    setTheme(name) {
        PluginsHandler.theme = name
    }

    getTheme() {
        return PluginsHandler.theme
    }

    setActivePlugins(activePlugins) {
        // inject the 'default' plugin as first one
        if(activePlugins.indexOf('default') !== -1)
            activePlugins.splice(activePlugins.indexOf('default'), 1)

        activePlugins.unshift('default')
        //

        // inject tree theme plugin as last one
        let treeTheme = 'theme-' + this.treeTheme
        if(activePlugins.indexOf(treeTheme) !== -1)
            activePlugins.splice(activePlugins.indexOf(treeTheme), 1)

        activePlugins.push(treeTheme)
        //

        for(let index in activePlugins) {
            let pluginName = activePlugins[index]

            // register dynamic plugins
            if(typeOf(pluginName) === 'object') {
                let plugin = activePlugins[index]
                this.plugins.set(plugin.name, plugin)
                this.registerPlugin(plugin)
                continue
            }

            if( this.predefinedBehaviors.indexOf(pluginName) === -1 ) {
                console.error('Invalid Tree Plugin: ' + pluginName)
            } else {
                this.plugins.set(pluginName, require('./../behaviors/' + pluginName).default(this))
                this.registerPlugin(this.plugins.get(pluginName))
            }
        }

        return this
    }

    getActivePlugins() {
        return this.plugins
    }

    getPlugin(name) {
        return this.plugins.get(name)
    }

    registerPlugin(plugin) {

        // validate dependencies
        if( plugin.dependencies )
            this.validateDependencies(plugin.name, plugin.dependencies)

        // slot
        for( let slotName in plugin.slotsContent ) {
            if( ! this.slotsContent[slotName])
                this.slotsContent[slotName] = new Array()

            this.slotsContent[slotName].push(plugin.slotsContent[slotName])
        }
        //

        // mixins
        if(plugin.treeMixins)
            this.mixins.treeMixins.push(plugin.treeMixins)

        if(plugin.treeRowMixins)
            this.mixins.treeRowMixins.push(plugin.treeRowMixins)
        //

        // rowIf
        if(plugin.rowIf)
            this._rowIf += (this._rowIf.length ? ' || ' : '') + plugin.rowIf

        // rowShow
        if(plugin.rowShow)
            this._rowShow += (this._rowShow.length ? ' || ' : '') + plugin.rowShow

        // rowSubNodesIf
        if(plugin.rowSubNodesIf)
            this._rowSubNodesIf += (this._rowSubNodesIf.length ? ' || ' : '') + plugin.rowSubNodesIf

        // rowSubNodesShow
        if(plugin.rowSubNodesShow)
            this._rowSubNodesShow += (this._rowSubNodesShow.length ? ' || ' : '') + plugin.rowSubNodesShow
    }

    getSlotContent(slotName) {
        if( ! this.slotsContent[slotName])
            return ''

        if( slotName === 'rowAfterTitle' || slotName === 'rowAppend' )
            return this.slotsContent[slotName].reverse().join('')

        return this.slotsContent[slotName].join('')
    }

    getMixins(type) {
        return this.mixins[type]
    }

    processData(data, scope)
    {
        this.createInternalKeys(data)

        let processed = data

        this.getActivePlugins().forEach( (plugin, pluginName) => {
            if(plugin.processData)
                processed = plugin.processData(processed, scope)
        })

        return processed
    }

    createInternalKeys(data) {
        for(let i in data) {
            Vue.set(data[i], 'internal', {})

            if( data[i].children && data[i].children.length ) {
                this.createInternalKeys(data[i].children)
            }
        }
    }

    rowEvents(scope) {
        let events = {}

        this.getActivePlugins().forEach( (plugin, pluginName) => {
            if(plugin.rowEvents) {
                let pluginRowEvents = plugin.rowEvents(scope)

                for(let event in pluginRowEvents) {
                    if( ! events[event])
                        events[event] = []

                    if( typeOf(pluginRowEvents[event]) === 'array' ) {
                        for(let index in pluginRowEvents[event]) {
                            events[event].push(pluginRowEvents[event][index])
                        }
                    } else {
                        events[event].push(pluginRowEvents[event])
                    }
                }
            }
        })

        return events
    }

    treeClasses(scope) {
        let classes = []

        this.getActivePlugins().forEach( (plugin, pluginName) => {
            if(plugin.treeClasses)
                classes.push(plugin.treeClasses(scope))
        })

        return classes
    }

    rowClasses(scope) {
        let classes = []

        this.getActivePlugins().forEach( (plugin, pluginName) => {
            if(plugin.rowClasses)
                classes.push(plugin.rowClasses(scope))
        })

        return classes
    }

    rowAttrs(scope) {
        let attrs = {}

        this.getActivePlugins().forEach( (plugin, pluginName) => {
            if(plugin.rowAttrs)
                attrs = Object.assign(attrs, plugin.rowAttrs(scope))
        })

        return attrs
    }

    rowIf() {
        return (this._rowIf === '') ? true : this._rowIf
    }

    rowShow() {
        return (this._rowShow === '') ? true : this._rowShow
    }

    rowSubNodesIf() {
        return (this._rowSubNodesIf === '') ? true : this._rowSubNodesIf
    }

    rowSubNodesShow() {
        return (this._rowSubNodesShow === '') ? true : this._rowSubNodesShow
    }

    validateDependencies(pluginName, dependecy) {
        if(typeOf(dependecy) === 'array') {
            for(let i in dependecy) {
                this.validateDependencies(pluginName, dependecy[i])
            }
            return
        }

        if( ! this.plugins.has(dependecy)) {
            console.error(
                `<Tree> Plugin "${pluginName}" has dependency "${dependecy}".
                This should be specified before "${pluginName}".
                Please fix your "plugins" prop order.`
            )
        }
    }

    includePluginStyles() {
        // if this fails - then theme and active plugins are not set
        this.getActivePlugins().forEach( (plugin, pluginName) => {
            if(plugin.style)
                require('./../behaviors/' + pluginName + '/' + plugin.style)
        })
    }
}
